import React from "react";
import { Banknote, Wallet } from "lucide-react";
import SelfPaymentForm from "../SelfPaymentForm";
import { Button } from "../ui";
import Card from "../ui/Card";
import {
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  FormValidationAlert,
  SectionTitle,
} from "../createEvent/CreateEventFormUi";
import HostOnboardingStickyActions, {
  ONBOARDING_DESKTOP_ACTIONS,
} from "./HostOnboardingStickyActions";

const SELECTED_CARD = "border-ink/40 ring-1 ring-ink/15";
const IDLE_CARD = "border-black/[0.1] hover:border-ink/25";

/**
 * After school create — on-app, off-app, or skip. Sticky actions on mobile.
 */
export default function HostOnboardingPaymentStep({
  mode,
  onModeChange,
  payment,
  onPaymentChange,
  fieldErrors,
  payeeName,
  schoolName,
  saving,
  onBack,
  onContinue,
  onSkip,
}) {
  const errorCount = Object.keys(fieldErrors || {}).length;
  const showForm = mode === "on_app";
  const schoolLabel = (schoolName || "").trim();
  const continueLabel = saving ? "Saving…" : "Continue →";
  const canContinue = Boolean(mode) && !saving;

  return (
    <div className="space-y-5" data-testid="host-onboarding-payment-step" aria-busy={saving}>
      <FormValidationAlert count={errorCount} />

      <Card className={FORM_STYLE_CARD_CLASS}>
        <SectionTitle>How will students pay?</SectionTitle>
        <FieldHint className="text-[13px]">
          {schoolLabel ? (
            <>
              Optional for{" "}
              <span
                className="font-medium text-ink/85 inline-block max-w-[min(100%,28ch)] truncate align-bottom"
                title={schoolLabel}
              >
                {schoolLabel}
              </span>
              {" — "}
            </>
          ) : (
            "Optional — "
          )}
          change anytime later in Host settings.
        </FieldHint>

        <div className="mt-5 grid gap-3" role="radiogroup" aria-label="Payment preference">
          <button
            type="button"
            role="radio"
            aria-checked={mode === "on_app"}
            disabled={saving}
            onClick={() => onModeChange("on_app")}
            data-testid="host-onboarding-payment-on-app"
            className={`text-left rounded-lg2 border bg-surface p-4 min-h-[44px] motion-safe:transition-colors motion-safe:duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 disabled:opacity-50 ${
              mode === "on_app" ? SELECTED_CARD : IDLE_CARD
            }`}
          >
            <span className="inline-flex items-center gap-2 font-ui font-semibold text-[15px]">
              <Wallet className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
              Share details on the app
            </span>
            <span className="mt-1 block font-ui text-[13px] text-ink/70 leading-snug text-pretty">
              Students see your link or UPI when they book.
            </span>
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={mode === "off_app"}
            disabled={saving}
            onClick={() => onModeChange("off_app")}
            data-testid="host-onboarding-payment-off-app"
            className={`text-left rounded-lg2 border bg-surface p-4 min-h-[44px] motion-safe:transition-colors motion-safe:duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 disabled:opacity-50 ${
              mode === "off_app" ? SELECTED_CARD : IDLE_CARD
            }`}
          >
            <span className="inline-flex items-center gap-2 font-ui font-semibold text-[15px]">
              <Banknote className="w-4 h-4 shrink-0" strokeWidth={2} aria-hidden />
              I’ll handle it off the app
            </span>
            <span className="mt-1 block font-ui text-[13px] text-ink/70 leading-snug text-pretty">
              Cash, transfers, or whatever you already use — nothing shown here.
            </span>
          </button>
        </div>
      </Card>

      {showForm ? (
        <Card className={FORM_STYLE_CARD_CLASS} data-testid="host-onboarding-payment-form">
          <SectionTitle icon={Wallet}>Payment method</SectionTitle>
          <FieldHint className="text-[13px]">
            Optional for now — in-app payment turns on when you add a link or UPI.
          </FieldHint>
          <SelfPaymentForm
            values={payment}
            onChange={onPaymentChange}
            payeeName={payeeName}
            idPrefix="host-onboarding-payment"
            fieldErrors={fieldErrors}
            error={fieldErrors.payment}
            showQrPreview={false}
          />
        </Card>
      ) : null}

      <div className={ONBOARDING_DESKTOP_ACTIONS}>
        <Button
          type="button"
          variant="secondary"
          disabled={saving}
          onClick={onBack}
          data-testid="host-onboarding-payment-back"
        >
          ← Back
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {onSkip ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              disabled={saving}
              onClick={onSkip}
              data-testid="host-onboarding-payment-skip"
              className="min-h-[44px] px-2"
            >
              Skip for now
            </Button>
          ) : null}
          <Button
            type="button"
            variant="primary"
            size="lg"
            disabled={!canContinue}
            onClick={onContinue}
            data-testid="host-onboarding-payment-continue"
          >
            {continueLabel}
          </Button>
        </div>
      </div>

      {!mode ? (
        <p className="font-ui text-[13px] text-ink/65 text-pretty sm:mb-0 mb-1" role="status">
          Pick an option above, or skip and set this up later.
        </p>
      ) : null}

      <HostOnboardingStickyActions testId="host-onboarding-payment-sticky">
        <Button
          type="button"
          variant="secondary"
          disabled={saving}
          onClick={onBack}
          className="shrink-0 px-3"
          data-testid="host-onboarding-payment-back-sticky"
        >
          Back
        </Button>
        {onSkip ? (
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={onSkip}
            className="shrink-0 px-3 text-[13px]"
            data-testid="host-onboarding-payment-skip-sticky"
          >
            Skip
          </Button>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="lg"
          disabled={!canContinue}
          onClick={onContinue}
          className="flex-1 min-w-0"
          data-testid="host-onboarding-payment-continue-sticky"
        >
          <span className="truncate">{continueLabel}</span>
        </Button>
      </HostOnboardingStickyActions>
    </div>
  );
}
